export interface BasketTestItem {
  description: string;
  imageUrl: string;
  quantity: string;
  value: string;
}

export interface TestAdditionalData {
  currencyCode: string;
  basketLink: string;
  items: BasketTestItem[];
}

export const DEFAULT_BASKET_TEST_ITEM: Readonly<BasketTestItem> =
  Object.freeze({
    description: "Product Description",
    imageUrl: "",
    quantity: "1",
    value: "10.00",
  });

export const DEFAULT_TEST_ADDITIONAL_DATA: Readonly<TestAdditionalData> =
  Object.freeze({
    currencyCode: "GBP",
    basketLink: "",
    items: [{ ...DEFAULT_BASKET_TEST_ITEM }],
  });

const escapeXml = (value: unknown = "") =>
  String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");

const unescapeXml = (value: unknown = "") =>
  String(value)
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&quot;", '"')
    .replaceAll("&apos;", "'")
    .replaceAll("&amp;", "&");

const getXmlTagValue = (xml: string, tagName: string) => {
  const expression = new RegExp(
    `<${tagName}>([\\s\\S]*?)</${tagName}>`,
    "i"
  );
  const match = expression.exec(xml);

  return match ? unescapeXml(match[1].trim()) : "";
};

export const sanitizeBasketItemQuantity = (rawValue: unknown) => {
  const parsed = Number.parseInt(String(rawValue ?? ""), 10);

  return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : "1";
};

export const sanitizeBasketItemValue = (rawValue: unknown) => {
  const parsed = Number.parseFloat(String(rawValue ?? ""));

  return Number.isFinite(parsed) && parsed >= 0
    ? parsed.toFixed(2)
    : "0.00";
};

export const normalizeBasketTestItems = (
  rawValue: string
): BasketTestItem[] => {
  try {
    const parsed = JSON.parse(rawValue);

    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [{ ...DEFAULT_BASKET_TEST_ITEM }];
    }

    return parsed.map((item) => ({
      description: item?.description ?? "",
      imageUrl: item?.imageUrl ?? "",
      quantity: sanitizeBasketItemQuantity(item?.quantity),
      value: sanitizeBasketItemValue(item?.value),
    }));
  } catch {
    return [{ ...DEFAULT_BASKET_TEST_ITEM }];
  }
};

export const stringifyBasketTestItems = (items: unknown) =>
  JSON.stringify(items);

const parsePositiveNumber = (rawValue: string) => {
  const value = Number.parseFloat(rawValue);

  return Number.isFinite(value) && value > 0 ? value : 0;
};

export const deriveBasketOrderValue = (items: BasketTestItem[]) => {
  const normalizedItems = normalizeBasketTestItems(
    stringifyBasketTestItems(items)
  );
  const total = normalizedItems.reduce((sum, item) => {
    const quantity = parsePositiveNumber(item.quantity);
    const value = parsePositiveNumber(item.value);

    return sum + quantity * value;
  }, 0);

  return total.toFixed(2);
};

export const parseTestAdditionalData = (
  rawValue: unknown
): TestAdditionalData => {
  const xml = typeof rawValue === "string" ? rawValue.trim() : "";

  if (!xml || !xml.includes("<Basket>")) {
    return {
      ...DEFAULT_TEST_ADDITIONAL_DATA,
      items: [{ ...DEFAULT_BASKET_TEST_ITEM }],
    };
  }

  const itemMatches = [...xml.matchAll(/<Item>([\s\S]*?)<\/Item>/gi)];
  const parsedItems = itemMatches.map(([, itemXml]) => ({
    description: getXmlTagValue(itemXml, "Description"),
    imageUrl: getXmlTagValue(itemXml, "ImageUrl"),
    quantity: getXmlTagValue(itemXml, "Quantity"),
    value: getXmlTagValue(itemXml, "Value"),
  }));

  return {
    currencyCode: DEFAULT_TEST_ADDITIONAL_DATA.currencyCode,
    basketLink: getXmlTagValue(xml, "BasketLink"),
    items:
      parsedItems.length > 0
        ? normalizeBasketTestItems(stringifyBasketTestItems(parsedItems))
        : [{ ...DEFAULT_BASKET_TEST_ITEM }],
  };
};

export const toTestAdditionalDataXml = (
  testData?: Partial<TestAdditionalData> | null
) => {
  const items = normalizeBasketTestItems(
    stringifyBasketTestItems(testData?.items)
  );
  const orderValue = deriveBasketOrderValue(items);
  const currencyCode = DEFAULT_TEST_ADDITIONAL_DATA.currencyCode;
  const basketLink = (testData?.basketLink || "").trim();

  const itemsXml = items
    .map(
      (item) => `<Item>
      <Description>${escapeXml(item.description)}</Description>
      <ImageUrl>${escapeXml(item.imageUrl)}</ImageUrl>
      <Quantity>${escapeXml(item.quantity)}</Quantity>
      <Value>${escapeXml(item.value)}</Value>
    </Item>`
    )
    .join("\n");

  return `<Basket>
  <OrderValue>${escapeXml(orderValue)}</OrderValue>
  <CurrencyCode>${escapeXml(currencyCode)}</CurrencyCode>
  ${
    basketLink
      ? `<BasketLink>${escapeXml(basketLink)}</BasketLink>`
      : ""
  }
  <Items>
    ${itemsXml}
  </Items>
</Basket>`;
};
